import { useState, useEffect } from 'react';

const CACHE_KEY = 'geoLocation';

const COUNTRY_NAME_MAP = {
  'Australia': 'Australia',
  'United States': 'United States',
  'Canada': 'Canada',
  'New Zealand': 'New Zealand',
  'United Kingdom': 'United Kingdom',
};

const getSessionCache = () => {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
};

const setSessionCache = (data) => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    // sessionStorage unavailable
  }
};

const DEFAULT_COUNTRY = 'Australia';
const DEFAULT_STATE = 'NSW';

const useGeoLocation = (availableCountries) => {
  const [geoCountry, setGeoCountry] = useState(null);
  const [geoState, setGeoState] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const cached = getSessionCache();
    if (cached !== null) {
      setGeoCountry(cached.country || null);
      setGeoState(cached.state || null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    fetch('http://ip-api.com/json/', { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error('Geo API failed');
        return res.json();
      })
      .then((data) => {
        if (data.status !== 'success') throw new Error('Geo API failed');
        const country = COUNTRY_NAME_MAP[data.country] || null;
        const state = data.region || null;
        setSessionCache({ country, state });
        setGeoCountry(country);
        setGeoState(state);
      })
      .catch(() => {
        setSessionCache({ country: null, state: null });
        setGeoCountry(null);
        setGeoState(null);
      })
      .finally(() => {
        clearTimeout(timeoutId);
        setLoading(false);
      });

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, []);

  const resolvedCountry =
    geoCountry && availableCountries?.[geoCountry]
      ? geoCountry
      : DEFAULT_COUNTRY;

  // New Zealand is a single national jurisdiction: the ip-api "region" is a NZ
  // council area (e.g. "Auckland") that classifies AU on the backend, so never
  // let it survive as the curriculum state. Coerce NZ to the "NZ" sentinel
  // (never "NSW").
  const resolvedState =
    resolvedCountry === 'New Zealand'
      ? 'NZ'
      : geoState && availableCountries?.[resolvedCountry]?.includes(geoState)
        ? geoState
        : resolvedCountry === DEFAULT_COUNTRY
          ? DEFAULT_STATE
          : availableCountries?.[resolvedCountry]?.[0] || DEFAULT_STATE;

  return { geoCountry: resolvedCountry, geoState: resolvedState, loading };
};

export default useGeoLocation;
